import type { DrawingDocument } from '../model';
import { moleculeEdges } from './documentSelectors.ts';
import { moleculeComponents, REACTION_ZONES, type BoxedMolecule, type MoleculeComponent } from './reactionGraph.ts';

/** One bolt the paper draws between a salt's cation and an anion (ADR 0022 as amended). */
export interface IonicPair {
  readonly id: string;
  readonly cationId: string;
  readonly anionId: string;
}

/**
 * ADR 0022 (as amended 2026-10-27, owner): the ionic bonds are not stored -- a salt is its separate
 * ions, as IUPAC draws it -- but the paper shows a bolt between them, so a student sees what holds
 * the salt together. Worked out from the drawing:
 *
 * - The salt is the charged pieces of the paper whose charges add up to zero, two or more of them
 *   (#1086's rule). Neutral pieces beside it don't count.
 * - Cations and anions of different pieces are paired closest first, each ion taking as many
 *   partners as its charge (Ca2+ two Cl-; a Cl- one cation), so one anion never holds every
 *   cation. Distances use the document's own coordinates, so a drag in progress stretches a bolt
 *   rather than re-pairing it.
 */
export function ionicPairs(document: DrawingDocument): readonly IonicPair[] {
  const { structure } = document;
  const coordinates = document.depiction.coordinateSet.coordinates;
  const pieces = moleculeComponents(
    structure.graph.nodes.map((node) => node.id),
    moleculeEdges(structure),
    (id) => coordinates[id]
  );
  return pairIons(document, pieces);
}

/**
 * The bolts between the ions among {@code pieces} -- the charged ones, when their charges add up
 * to zero (#1086's rule), paired closest first as {@link ionicPairs} describes.
 */
function pairIons(document: DrawingDocument, pieces: readonly MoleculeComponent[]): readonly IonicPair[] {
  const { structure } = document;
  const coordinates = document.depiction.coordinateSet.coordinates;
  const charge = (id: string) => structure.formalCharges[id] ?? 0;
  const pieceCharge = (atomIds: readonly string[]) => atomIds.reduce((sum, id) => sum + charge(id), 0);
  const charged = pieces.filter(({ atomIds }) => pieceCharge(atomIds) !== 0);
  if (charged.length < 2 || charged.reduce((sum, { atomIds }) => sum + pieceCharge(atomIds), 0) !== 0) return [];

  const pieceOf = new Map<string, number>();
  charged.forEach(({ atomIds }, index) => atomIds.forEach((id) => pieceOf.set(id, index)));
  const atoms = [...pieceOf.keys()];
  const anions = atoms.filter((id) => charge(id) < 0);
  const distance = (a: string, b: string) => {
    const [p, q] = [coordinates[a], coordinates[b]];
    return p && q ? Math.hypot(p.x - q.x, p.y - q.y) : Infinity;
  };
  const remaining = new Map(atoms.map((id) => [id, Math.abs(charge(id))]));
  const candidates = atoms
    .filter((id) => charge(id) > 0)
    .flatMap((cation) =>
      anions
        .filter((anion) => pieceOf.get(anion) !== pieceOf.get(cation))
        .map((anion) => ({ cation, anion, distance: distance(cation, anion) }))
    )
    .sort((a, b) => a.distance - b.distance || a.cation.localeCompare(b.cation) || a.anion.localeCompare(b.anion));
  const pairs: IonicPair[] = [];
  for (const { cation, anion } of candidates) {
    if (!remaining.get(cation) || !remaining.get(anion)) continue;
    remaining.set(cation, remaining.get(cation)! - 1);
    remaining.set(anion, remaining.get(anion)! - 1);
    pairs.push({ id: `ionic:${cation}:${anion}`, cationId: cation, anionId: anion });
  }
  return pairs;
}

/**
 * #1134 (owner): in a reaction box, a salt's ions are one structure -- one participant, with no
 * "+" between a cation and its anion. The ions of each box are paired as {@link ionicPairs} pairs
 * them (within that box only, so a salt is judged where it stands), and the paired pieces are
 * joined into one: their atoms together, their bounds united. Loose molecules are left as they are.
 */
export function saltsAsOne(document: DrawingDocument, molecules: readonly BoxedMolecule[]): BoxedMolecule[] {
  const joined: BoxedMolecule[] = molecules.filter((molecule) => molecule.zone === null);
  for (const zone of REACTION_ZONES) {
    const members = molecules.filter((molecule) => molecule.zone === zone);
    // Union-find over the box's members, joined along each bolt.
    const parent = members.map((_, index) => index);
    const root = (index: number): number => (parent[index] === index ? index : (parent[index] = root(parent[index]!)));
    const memberOf = new Map<string, number>();
    members.forEach(({ atomIds }, index) => atomIds.forEach((id) => memberOf.set(id, index)));
    for (const { cationId, anionId } of pairIons(document, members)) {
      parent[root(memberOf.get(cationId)!)] = root(memberOf.get(anionId)!);
    }
    const groups = new Map<number, BoxedMolecule[]>();
    members.forEach((member, index) => groups.set(root(index), [...(groups.get(root(index)) ?? []), member]));
    for (const group of groups.values()) {
      joined.push(
        group.length === 1
          ? group[0]!
          : {
              atomIds: group.flatMap((member) => member.atomIds),
              bounds: {
                minX: Math.min(...group.map((member) => member.bounds.minX)),
                minY: Math.min(...group.map((member) => member.bounds.minY)),
                maxX: Math.max(...group.map((member) => member.bounds.maxX)),
                maxY: Math.max(...group.map((member) => member.bounds.maxY))
              },
              zone
            }
      );
    }
  }
  return joined;
}
